import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Card, CardContent, Stack, TextField, Button, Grid, Chip, Pagination, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import jerseyApi from '../api/jerseyApi';
import PreviewFrame from '../components/PreviewFrame';
import JerseyEditor from '../components/JerseyEditor/JerseyEditor';

interface ApprovedScheme {
  id: number;
  original_string: string;
  normalised_string: string;
  approved: boolean;
  preview_string?: string | null;
  times_used?: number;
  front_svg_path?: string | null;
  side_svg_path?: string | null;
  front_svg_url?: string | null;
  side_svg_url?: string | null;
  front_svg?: string | null;
  side_svg?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
}

const pageSize = 10;

const MeetingManagement: React.FC = () => {
  const toPublicS3 = (p?: string | null): string | undefined => {
    if (!p) return undefined;
    const CDN_HOST = 'silks.medialityracing.com.au';
    const PROD_BUCKET = 'mr-cen-silks-public-prd';

    // Handle s3://bucket/key URIs
    if (p.startsWith('s3://')) {
      const rest = p.slice('s3://'.length);
      const slash = rest.indexOf('/');
      if (slash > 0) {
        const bucket = rest.slice(0, slash);
        const key = rest.slice(slash + 1);
        if (bucket === PROD_BUCKET) {
          return `https://${CDN_HOST}/${encodeURI(key)}`;
        }
        return `https://${bucket}.s3.ap-southeast-2.amazonaws.com/${encodeURI(key)}`;
      }
    }

    // Handle already-HTTP URLs
    if (p.startsWith('http://') || p.startsWith('https://')) {
      try {
        const u = new URL(p);
        // Path-style: https://s3.ap-southeast-2.amazonaws.com/mr-cen-silks-public-prd/key
        if (u.hostname === 's3.ap-southeast-2.amazonaws.com' && u.pathname.startsWith(`/${PROD_BUCKET}/`)) {
          return `https://${CDN_HOST}${u.pathname.replace(`/${PROD_BUCKET}`, '')}${u.search}`;
        }
        // Virtual-hosted: https://mr-cen-silks-public-prd.s3.ap-southeast-2.amazonaws.com/key
        if (u.hostname === `${PROD_BUCKET}.s3.ap-southeast-2.amazonaws.com`) {
          return `https://${CDN_HOST}${u.pathname}${u.search}`;
        }
        return p;
      } catch {
        return p;
      }
    }

    return undefined;
  };

  const addCacheBuster = (url?: string, updatedAt?: string | null): string | undefined => {
    if (!url) return undefined;
    // Use updatedAt timestamp when available so image refreshes after save
    let v = Date.now();
    if (updatedAt) {
      const t = new Date(updatedAt).getTime();
      if (!Number.isNaN(t)) v = t;
    }
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}v=${v}`;
  };
  const [items, setItems] = useState<ApprovedScheme[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [, setLoading] = useState(false);

  // Editor dialog state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorOriginal, setEditorOriginal] = useState<string>('');
  const [editorEditable, setEditorEditable] = useState<string>('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
      const { items, total } = await jerseyApi.listApprovedSchemes({
        skip: (page - 1) * pageSize,
        limit: pageSize,
        search: search || undefined,
        include_svg: isLocal, // only inline SVGs in local dev to support filesystem paths
      });
      setItems(items);
      setTotal(total);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openEditorForRow = (row: ApprovedScheme) => {
    const original = row.original_string || '';
    const editable = (row.preview_string && row.preview_string.trim().length > 0) ? row.preview_string : original;
    setEditorOriginal(original);
    setEditorEditable(editable || '');
    setEditorOpen(true);
  };

  const handleDelete = async (i: number) => {
    const row = items[i];
    await jerseyApi.deleteApprovedScheme(row.id);
    fetchData();
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Approved Silks
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1}>
            <TextField size="small" placeholder="Search by original string" value={search} onChange={e => setSearch(e.target.value)} />
            <Button variant="outlined" onClick={() => { setPage(1); fetchData(); }}>Search</Button>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {items.map((row, i) => (
          <Grid item xs={12} md={6} key={row.id}>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1">#{row.id}</Typography>
                  {row.approved ? <Chip color="success" label="Approved" size="small" /> : <Chip color="warning" label="Not Approved" size="small" />}
                </Stack>
                <TextField
                  label="Original String"
                  fullWidth
                  value={row.original_string}
                  InputProps={{ readOnly: true }}
                  sx={{ mt: 1 }}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" startIcon={<Edit />} variant="contained" onClick={() => openEditorForRow(row)}>Edit</Button>
                  <Button size="small" startIcon={<Delete />} color="error" onClick={() => handleDelete(i)}>Delete</Button>
                </Stack>

                <Box sx={{ mt: 2 }}>
                  <PreviewFrame label="Front View" src={addCacheBuster(row.front_svg_url || toPublicS3(row.front_svg_path), row.updated_at)} svg={row.front_svg || undefined} />
                </Box>
                <Box sx={{ mt: 1 }}>
                  <PreviewFrame label="Side View" src={addCacheBuster(row.side_svg_url || toPublicS3(row.side_svg_path), row.updated_at)} svg={row.side_svg || undefined} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Edit Approved Silk</DialogTitle>
        <DialogContent>
          <JerseyEditor
            key={`${editorOriginal}::${editorEditable}`}
            initialOriginalString={editorOriginal}
            initialDescription={editorEditable}
            onCancel={() => setEditorOpen(false)}
            onSave={() => {
              setEditorOpen(false);
              fetchData();
            }}
          />
        </DialogContent>
      </Dialog>


      <Stack alignItems="center" sx={{ mt: 2 }}>
        <Pagination count={Math.ceil(total / pageSize)} page={page} onChange={(_, p) => setPage(p)} />
      </Stack>
    </Box>
  );
};

export default MeetingManagement;
